import React, { useState } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { FaChevronLeft, FaChevronRight } from 'react-icons/fa';
import moment from 'moment';
import { DataTable } from '../../../components/EntityList';

const PAGE_SIZE = 5;

/**
 * "Próximos vencimientos" panel from the Inicio handoff spec — the next 14
 * days of unpaid dues, fed by the same GeneratedDebtService.search() call
 * shape Dashboard.jsx already uses for the pending/overdue-this-month KPIs,
 * just windowed differently. No new backend endpoint needed.
 *
 * Fixed height + client-side pagination: without this, the panel's border
 * stretched to match ActivityFeed's height (CSS Grid's default
 * align-items: stretch) leaving blank space below a short table.
 */
function UpcomingDues({ dues, formatCurrency, loading }) {
	const navigate = useNavigate();
	const [page, setPage] = useState(1);

	const totalPages = Math.max(1, Math.ceil(dues.length / PAGE_SIZE));
	const pagedDues = dues.slice((page - 1) * PAGE_SIZE, page * PAGE_SIZE);

	const columns = [
		{
			key: 'expiration',
			header: 'Fecha',
			className: 'tw:font-mono',
			render: (due) => moment(due.expiration).format('DD/MM/YYYY'),
		},
		{
			key: 'account',
			header: 'Cuenta',
			render: (due) => due.debt?.owner?.name || '—',
		},
		{
			key: 'concept',
			header: 'Concepto',
			className: 'tw:text-ink-2',
			render: (due) => due.debt?.concept || '—',
		},
		{
			key: 'amount',
			header: 'Monto',
			className: 'tw:text-right tw:font-mono',
			render: (due) => {
				const amount = due.unpaidAmount ?? due.amount ?? 0;
				return formatCurrency(amount, due.debt?.currency);
			},
		},
	];

	return (
		<section className='tw:h-[420px] tw:flex tw:flex-col tw:bg-surface tw:border tw:border-border tw:rounded-panel tw:overflow-hidden'>
			<div className='tw:px-4 tw:py-3 tw:border-b tw:border-border tw:flex tw:items-center tw:justify-between'>
				<h3 className='tw:m-0 tw:text-[13px] tw:font-semibold tw:text-ink-0'>Próximos vencimientos</h3>
				<Link
					to='/AdminScreen/reports/debtDues/search'
					className='tw:text-[12.5px] tw:font-medium tw:text-accent tw:no-underline tw:hover:underline'
				>
					Ver todos →
				</Link>
			</div>
			<div className='tw:flex-1 tw:overflow-y-auto'>
				<DataTable
					bare
					columns={columns}
					rows={pagedDues}
					rowKey={(due) => due._id}
					onRowClick={(due) => due.debt?.owner?._id && navigate(`/AdminScreen/accounts/account/${due.debt.owner._id}`)}
					emptyMessage='No hay vencimientos en los próximos 14 días.'
					loading={loading}
					skeleton
					skeletonRows={PAGE_SIZE}
				/>
			</div>
			{dues.length > PAGE_SIZE && (
				<div className='tw:flex tw:items-center tw:justify-between tw:px-4 tw:py-2 tw:border-t tw:border-border tw:text-[12px] tw:text-ink-3'>
					<span>
						{(page - 1) * PAGE_SIZE + 1}–{Math.min(page * PAGE_SIZE, dues.length)} de {dues.length}
					</span>
					<div className='tw:flex tw:gap-1'>
						<button
							type='button'
							className='tw:w-7 tw:h-7 tw:rounded-token tw:flex tw:items-center tw:justify-center tw:text-ink-2 tw:hover:bg-surface-2 tw:disabled:opacity-40 tw:bg-transparent tw:border-0'
							onClick={() => setPage((p) => p - 1)}
							disabled={page === 1}
						>
							<FaChevronLeft size={12} />
						</button>
						<button
							type='button'
							className='tw:w-7 tw:h-7 tw:rounded-token tw:flex tw:items-center tw:justify-center tw:text-ink-2 tw:hover:bg-surface-2 tw:disabled:opacity-40 tw:bg-transparent tw:border-0'
							onClick={() => setPage((p) => p + 1)}
							disabled={page === totalPages}
						>
							<FaChevronRight size={12} />
						</button>
					</div>
				</div>
			)}
		</section>
	);
}

export default UpcomingDues;
